<template>
  <div class="page-outer">
    <div class="page-header page-header-fixed-form">
      <div class="header-left">
        <h1 class="page-title">{{ pageTitle }}</h1>
      </div>
      <div class="header-actions">
        <router-link :to="{ name: 'CreateShareRewardCampaign' }" class="btn btn-primary">
                <i class="ri-add-line"></i>
                新增分享換好康活動
              </router-link>
      </div>
    </div>

    <div class="page-container">
    <div class="liff-entry-card">
      <div class="liff-entry-title">LIFF 進入連結</div>
      <p class="liff-entry-hint">同一時間只會有一檔啟用中的活動。會員開啟此連結後，會進入目前啟用的活動，換活動時不用更換網址。</p>
      <div v-if="liffEntries.length === 0" class="liff-url-missing">尚未設定共用 LIFF ID</div>
      <div v-for="entry in liffEntries" :key="entry.id" class="liff-url-row">
        <div v-if="liffEntries.length > 1" class="liff-oa-name">{{ entry.name }}</div>
        <code class="liff-url-code">{{ entry.url }}</code>
        <button type="button" class="btn btn-sm btn-secondary" @click="copyLiffUrl(entry)">
          <i :class="copiedId === entry.id ? 'ri-check-line' : 'ri-file-copy-line'"></i>
          {{ copiedId === entry.id ? '已複製' : '複製連結' }}
        </button>
      </div>
    </div>

    <div class="page-toolbar">
      <div class="filter-group">
        <input
          v-model="searchQuery"
          type="text"
          class="form-control"
          placeholder="搜尋活動名稱或代碼..."
          @input="handleSearch"
        />
      </div>
      <div class="filter-group">
        <select v-model="filterStatus" class="form-control" @change="handleFilterChange">
          <option value="">全部狀態</option>
          <option value="active">啟用</option>
          <option value="inactive">停用</option>
        </select>
      </div>
    </div>

    <div v-if="loading" class="loading-state">
      <div class="spinner"></div>
      <p>載入中...</p>
    </div>

    <div v-else-if="error" class="error-state">
      <p class="error-message">{{ error }}</p>
      <button class="btn btn-secondary" @click="fetchCampaigns">重試</button>
    </div>

    <div v-else class="table-wrapper">
      <table>
        <thead>
          <tr>
            <th>活動代碼</th>
            <th>活動名稱</th>
            <th>LINE OA</th>
            <th>狀態</th>
            <th>活動期間</th>
            <th>建立時間</th>
            <th>操作</th>
          </tr>
        </thead>
        <tbody>
          <tr v-if="campaigns.length === 0">
            <td colspan="7" class="empty-state">沒有找到活動</td>
          </tr>
          <tr v-for="campaign in campaigns" :key="campaign.id">
            <td>{{ campaign.campaignCode }}</td>
            <td>{{ campaign.campaignName }}</td>
            <td>{{ lineConfigName(campaign) }}</td>
            <td>
              <span :class="campaign.isActive ? 'badge badge-success' : 'badge badge-secondary'">
                {{ campaign.isActive ? '啟用' : '停用' }}
              </span>
            </td>
            <td>{{ formatPeriod(campaign.startAt, campaign.endAt) }}</td>
            <td>{{ formatDate(campaign.createdAt) }}</td>
            <td>
              <div class="action-buttons">
                <router-link
                  :to="{ name: 'EditShareRewardCampaign', params: { id: campaign.id } }"
                  class="btn btn-sm btn-primary"
                >
                  <i class="ri-edit-line"></i>
                  編輯
                </router-link>
                <button class="btn btn-sm btn-danger" @click="handleDelete(campaign)">
                  <i class="ri-delete-bin-line"></i>
                  刪除
                </button>
              </div>
            </td>
          </tr>
        </tbody>
      </table>

      <ListPagination
        v-if="pagination.totalPages > 1"
        :page="currentPage"
        :total-pages="pagination.totalPages"
        :total="pagination.totalItems"
        @change="goToPage"
      />
    </div>
    </div>
  </div>
</template>

<script>
import { ref, reactive, onMounted } from 'vue';
import { usePageTitle } from '@/composables/usePageTitle.js';
import { shareRewardCampaignService } from '@/api/modules/shareRewardCampaigns.js';
import { lineService } from '@/api/modules/line.js';
import { showDeleteConfirm, showError, showSuccess } from '@/utils/swal.js';

export default {
  name: 'ShareRewardCampaignList',
  setup() {
    const { pageTitle } = usePageTitle('分享換好康');
    const campaigns = ref([]);
    const loading = ref(false);
    const error = ref(null);
    const searchQuery = ref('');
    const filterStatus = ref('');
    const currentPage = ref(1);
    const pageSize = ref(20);
    const pagination = reactive({
      totalItems: 0,
      totalPages: 1
    });
    const lineConfigMap = ref({});
    const liffEntries = ref([]);
    const copiedId = ref('');

    const loadLineConfigs = async () => {
      try {
        const response = await lineService.getConfigs({ page: 1, pageSize: 100 });
        if (response?.success && response.data) {
          const items = response.data.items || response.data || [];
          const map = {};
          const entries = [];
          items.forEach((cfg) => {
            const ids = Array.isArray(cfg.liffIds) ? cfg.liffIds : [];
            const liffId = ids[0] || '';
            map[cfg.id] = {
              name: cfg.configName || '',
              liffId
            };
            if (liffId) {
              entries.push({
                id: cfg.id,
                name: cfg.configName || 'LINE OA',
                url: `https://liff.line.me/${liffId}/share-reward`
              });
            }
          });
          lineConfigMap.value = map;
          liffEntries.value = entries;
        }
      } catch (err) {
        console.error('載入 LINE OA 設定失敗:', err);
      }
    };

    const lineConfigName = (campaign) => {
      return lineConfigMap.value[campaign.lineConfigId]?.name || campaign.lineConfigName || '-';
    };

    const copyLiffUrl = async (entry) => {
      if (!entry?.url) return;
      try {
        await navigator.clipboard.writeText(entry.url);
      } catch {
        const el = document.createElement('textarea');
        el.value = entry.url;
        el.setAttribute('readonly', '');
        el.style.position = 'fixed';
        el.style.left = '-9999px';
        document.body.appendChild(el);
        el.select();
        document.execCommand('copy');
        document.body.removeChild(el);
      }
      copiedId.value = entry.id;
      setTimeout(() => {
        if (copiedId.value === entry.id) copiedId.value = '';
      }, 2000);
    };

    const fetchCampaigns = async () => {
      loading.value = true;
      error.value = null;
      try {
        const params = {
          page: currentPage.value,
          pageSize: pageSize.value
        };
        if (searchQuery.value) {
          params.search = searchQuery.value;
        }
        if (filterStatus.value === 'active') {
          params.isActive = true;
        } else if (filterStatus.value === 'inactive') {
          params.isActive = false;
        }

        const response = await shareRewardCampaignService.getCampaigns(params);
        if (response?.success && response.data) {
          campaigns.value = response.data.items || [];
          pagination.totalItems = response.data.pagination?.totalItems || 0;
          pagination.totalPages = response.data.pagination?.totalPages || 1;
        } else {
          error.value = response?.message || '獲取活動列表失敗';
        }
      } catch (err) {
        console.error('獲取活動列表失敗:', err);
        error.value = err.message || '獲取活動列表失敗';
      } finally {
        loading.value = false;
      }
    };

    const handleSearch = () => {
      currentPage.value = 1;
      fetchCampaigns();
    };

    const handleFilterChange = () => {
      currentPage.value = 1;
      fetchCampaigns();
    };

    const goToPage = (page) => {
      if (page >= 1 && page <= pagination.totalPages) {
        currentPage.value = page;
        fetchCampaigns();
      }
    };

    const handleDelete = async (campaign) => {
      const confirmed = await showDeleteConfirm(campaign.campaignName, [
        '門檻規則',
        '分享者進度',
        '邀請追蹤紀錄',
        '動作執行紀錄'
      ]);
      if (!confirmed) return;

      try {
        const response = await shareRewardCampaignService.deleteCampaign(campaign.id);
        if (response?.success) {
          await showSuccess('刪除成功', '活動已刪除');
          await fetchCampaigns();
        } else {
          await showError('刪除失敗', response?.message || '刪除活動失敗');
        }
      } catch (err) {
        console.error('刪除活動失敗:', err);
        await showError('刪除失敗', err.message || '刪除活動失敗');
      }
    };

    const formatDate = (dateString) => {
      if (!dateString) return '-';
      return new Date(dateString).toLocaleString('zh-TW');
    };

    const formatPeriod = (startAt, endAt) => {
      const start = startAt ? formatDate(startAt) : '立即開始';
      const end = endAt ? formatDate(endAt) : '無期限';
      return `${start} ~ ${end}`;
    };

    onMounted(() => {
      loadLineConfigs();
      fetchCampaigns();
    });

    return {
      pageTitle,
      campaigns,
      loading,
      error,
      searchQuery,
      filterStatus,
      currentPage,
      pagination,
      copiedId,
      liffEntries,
      fetchCampaigns,
      handleSearch,
      handleFilterChange,
      goToPage,
      handleDelete,
      formatDate,
      formatPeriod,
      lineConfigName,
      copyLiffUrl
    };
  }
};
</script>

<style scoped>
@import '~@/assets/scss/components/_list-page-common.scss';

.table-wrapper {
  background: white;
  border-radius: 8px;
  overflow-x: auto;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
}

table {
  width: 100%;
  min-width: 900px;
  border-collapse: collapse;
}

.liff-entry-card {
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 16px;
  margin-bottom: 16px;
}

.liff-entry-title {
  font-size: 15px;
  font-weight: 600;
  margin-bottom: 4px;
}

.liff-entry-hint {
  margin: 0 0 12px;
  color: #666;
  font-size: 13px;
}

.liff-url-row {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
}

.liff-url-row + .liff-url-row {
  margin-top: 10px;
}

.liff-oa-name {
  font-size: 13px;
  font-weight: 600;
  min-width: 120px;
}

.liff-url-code {
  flex: 1 1 240px;
  min-width: 0;
  font-size: 13px;
  line-height: 1.4;
  color: #444;
  background: #f5f5f5;
  padding: 8px 10px;
  border-radius: 4px;
  word-break: break-all;
}

.liff-url-missing {
  color: #999;
  font-size: 13px;
  white-space: nowrap;
}

thead {
  background-color: #f5f5f5;
}

th,
td {
  padding: 12px;
  text-align: left;
  border-bottom: 1px solid #e0e0e0;
}

tbody tr:hover {
  background-color: #f9f9f9;
}

.empty-state {
  text-align: center;
  color: #999;
  padding: 40px;
}

.action-buttons {
  display: flex;
  gap: 8px;
}

.pagination {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 16px;
  padding: 20px;
}
</style>
